// frontend/src/components/MergeClientsModal.jsx
import React, { useState } from 'react';

/**
 * Folds the clients ticked on the Clients tab into one survivor.
 *
 * `clients` is the SELECTION, not the whole client list -- you pick the
 * survivor from among the rows you ticked, and everything else in that
 * selection merges into it. onMerge(targetId, sourceIds) resolves with the
 * API response; if the backend had to leave tasks behind (an open task for
 * the same category and year already exists on the survivor) the modal stays
 * open and names them rather than closing on a half-done merge.
 */
export default function MergeClientsModal({ isOpen, onClose, clients, onMerge }) {
  const [targetId, setTargetId] = useState(null);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState('');
  // Set only once the merge has landed. Carries the survivor's name with it:
  // the page clears its selection on success, so `clients` is empty by the
  // time this view renders and cannot be read for it.
  const [outcome, setOutcome] = useState(null);

  if (!isOpen) return null;

  const target = clients.find((c) => c.id === targetId);
  const sources = clients.filter((c) => c.id !== targetId);

  const handleMerge = async () => {
    setError('');
    setMerging(true);
    try {
      const result = await onMerge(targetId, sources.map((c) => c.id));
      const skipped = result?.details?.skipped_tasks || [];
      if (skipped.length) {
        setOutcome({ skipped, targetName: target.name });
      } else {
        onClose();
      }
    } catch (err) {
      // api.js throws with the raw response body as `message` and the parsed
      // body on `response.data` -- prefer the latter or the modal shows a
      // literal blob of JSON.
      const data = err.response?.data;
      setError(data?.error || data?.detail || data?.message || err.message
               || 'Failed to merge clients');
    } finally {
      setMerging(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={panelStyle}>
        <h2 style={{ marginTop: 0, marginBottom: 20, fontSize: 20, fontWeight: 600 }}>
          Merge Clients
        </h2>

        {outcome ? (
          <>
            <div style={noticeStyle}>
              <strong>Merged.</strong>{' '}
              {outcome.skipped.length === 1
                ? '1 task could not be moved'
                : `${outcome.skipped.length} tasks could not be moved`}
              {' '}because {outcome.targetName} already has an open task for the
              same category and year.{' '}
              {outcome.skipped.length === 1 ? 'It stays' : 'They stay'}
              {' '}on the merged-away client:
              <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                {outcome.skipped.map((t) => (
                  <li key={t.id}>
                    {t.name} {t.fiscal_year} ({t.client})
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={primaryButtonStyle(false)}>Done</button>
            </div>
          </>
        ) : (
          <>
            <div style={warningStyle}>
              <strong>⚠️ Warning:</strong> Time entries, invoices, files, tasks,
              staff assignments and notes move to the client you keep. Every
              other selected client is marked inactive. This cannot be undone.
            </div>

            {error && <div style={errorStyle}>{error}</div>}

            <fieldset style={{ border: 'none', padding: 0, margin: '0 0 20px 0' }}>
              <legend style={{ marginBottom: 8, fontSize: 14, fontWeight: 500, padding: 0 }}>
                Which client do you want to keep?
              </legend>
              {clients.map((c) => (
                <label
                  key={c.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: `1px solid ${c.id === targetId ? '#0073ea' : '#e5e7eb'}`,
                    marginBottom: 6,
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                >
                  <input
                    type="radio"
                    name="merge-target"
                    checked={c.id === targetId}
                    onChange={() => setTargetId(c.id)}
                  />
                  <span>{c.name}{c.is_active === false ? ' (inactive)' : ''}</span>
                </label>
              ))}
            </fieldset>

            {target && (
              <div style={summaryStyle}>
                <p style={{ margin: '0 0 8px 0' }}>
                  <strong>
                    {sources.length === 1
                      ? `1 client will be merged into ${target.name}`
                      : `${sources.length} clients will be merged into ${target.name}`}
                    :
                  </strong>
                </p>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {sources.map((c) => <li key={c.id}>{c.name}</li>)}
                </ul>
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={onClose}
                disabled={merging}
                style={{
                  padding: '8px 16px',
                  borderRadius: 6,
                  border: '1px solid #e5e7eb',
                  backgroundColor: 'white',
                  cursor: merging ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleMerge}
                disabled={merging || !target}
                style={primaryButtonStyle(merging || !target)}
              >
                {merging ? 'Merging...' : 'Merge'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const panelStyle = {
  backgroundColor: 'white',
  borderRadius: 8,
  padding: 24,
  maxWidth: 600,
  width: '90%',
  maxHeight: '90vh',
  overflow: 'auto',
};

const warningStyle = {
  marginBottom: 20, padding: 12, backgroundColor: '#fef3c7',
  borderRadius: 6, fontSize: 14,
};

const noticeStyle = {
  marginBottom: 20, padding: 12, backgroundColor: '#fef3c7',
  borderRadius: 6, fontSize: 14,
};

const errorStyle = {
  marginBottom: 20, padding: 12, backgroundColor: '#fee2e2',
  color: '#dc2626', borderRadius: 6, fontSize: 14,
};

const summaryStyle = {
  marginBottom: 20, padding: 12, backgroundColor: '#f3f4f6',
  borderRadius: 6, fontSize: 13,
};

const primaryButtonStyle = (disabled) => ({
  padding: '8px 16px',
  borderRadius: 6,
  border: 'none',
  backgroundColor: disabled ? '#9ca3af' : '#ef4444',
  color: 'white',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: 14,
  fontWeight: 500,
});
